// ---------------------------------------------------------------------------
// Pure utility functions — keyword extraction, sentiment, formatting, helpers
// ---------------------------------------------------------------------------

import type { HartEvent, RiskInfo, TrendChange } from './types';
import {
  COMMON_BIGRAMS, DOMAIN_KEYWORDS, STOP_WORDS,
  ML_CAMPAIGN_TYPES,
  POSITIVE_PATTERNS, NEGATIVE_PATTERNS, NEUTRAL_PATTERNS,
  HIGH_RISK_WORDS, MED_RISK_WORDS, LOW_RISK_WORDS,
  SOURCE_ICON_MAP,
} from './constants';

// ── Keyword validation ──────────────────────────────────────────────────────

function looksLikeEnglish(word: string): boolean {
  if (!word || word.length < 3) return false;
  const w = word.toLowerCase();

  const vowels = (w.match(/[aeiouy]/g) || []).length;
  if (vowels === 0 && w.length > 2) return false;

  const vowelRatio = vowels / w.length;
  if (w.length >= 5 && (vowelRatio < 0.15 || vowelRatio > 0.7)) return false;
  if (/[bcdfghjklmnpqrstvwxz]{5,}/i.test(w)) return false;
  if (/(.)\1{2,}/i.test(w)) return false;

  let validBigrams = 0;
  for (let i = 0; i < w.length - 1; i++) {
    if (COMMON_BIGRAMS.has(w.substring(i, i + 2))) validBigrams++;
  }
  const bigramRatio = validBigrams / (w.length - 1);
  if (w.length >= 6 && bigramRatio < 0.2) return false;
  if (w.length >= 8 && bigramRatio < 0.25) return false;

  const suspiciousPatterns: RegExp[] = [
    /^[bcdfghjklmnpqrstvwxz]+$/i,
    /\d/,
    /[^a-z]/i,
    /^[aeiou]+$/i,
    /([a-z])\1{2,}/i,
    /[qxz]{2,}/i,
    /[^aeiou]{6,}/i,
  ];

  for (let idx = 0; idx < suspiciousPatterns.length; idx++) {
    if (suspiciousPatterns[idx].test(w)) {
      if (DOMAIN_KEYWORDS.has(w)) return true;
      if (idx === 3 && w.length <= 3) continue;
      return false;
    }
  }
  return true;
}

export function isValidKeyword(word: string): boolean {
  if (!word || typeof word !== 'string') return false;
  const w = word.toLowerCase().trim();
  if (w.length < 3 || w.length > 20) return false;
  if (DOMAIN_KEYWORDS.has(w)) return true;
  if (STOP_WORDS.has(w)) return false;
  if (/^\d+$/.test(w) || /\d/.test(w)) return false;
  if (/^(http|www|\.com|\.org|\.net|@)/i.test(w)) return false;
  return looksLikeEnglish(w);
}

export function filterKeywords(keywords: string[]): string[] {
  if (!keywords || !Array.isArray(keywords)) return [];
  const seen = new Set<string>();
  const filtered: string[] = [];

  for (const kw of keywords) {
    if (!kw || typeof kw !== 'string') continue;
    const word = kw.toLowerCase().trim();
    if (seen.has(word) || !isValidKeyword(word)) continue;
    seen.add(word);
    filtered.push(word);
  }

  filtered.sort((a, b) => {
    const aDomain = DOMAIN_KEYWORDS.has(a);
    const bDomain = DOMAIN_KEYWORDS.has(b);
    if (aDomain && !bDomain) return -1;
    if (!aDomain && bDomain) return 1;
    return b.length - a.length;
  });
  return filtered;
}

export function extractKeywordsFromText(text: string, maxKeywords = 10): string[] {
  if (!text || typeof text !== 'string') return [];
  let cleaned = text.toLowerCase()
    .replace(/https?:\/\/[^\s]+/g, ' ')
    .replace(/[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g, ' ')
    .replace(/[^a-z\s-]/g, ' ');

  const words = cleaned.match(/\b[a-z][a-z-]*[a-z]\b|\b[a-z]{3,}\b/g) || [];
  const wordCounts: Record<string, number> = {};
  for (const word of words) {
    const cleanWord = word.replace(/-/g, '');
    if (cleanWord.length >= 3 && isValidKeyword(cleanWord)) {
      wordCounts[cleanWord] = (wordCounts[cleanWord] || 0) + 1;
    }
  }

  return Object.entries(wordCounts)
    .sort((a, b) => {
      const aDomain = DOMAIN_KEYWORDS.has(a[0]);
      const bDomain = DOMAIN_KEYWORDS.has(b[0]);
      if (aDomain && !bDomain) return -1;
      if (!aDomain && bDomain) return 1;
      return b[1] - a[1];
    })
    .map(([word]) => word)
    .slice(0, maxKeywords);
}

// ── Sentiment analysis ──────────────────────────────────────────────────────

const NEWS_FILTER_WORDS = [
  'reported','announced','confirmed','stated','said','according','sources',
  'officials','ministry','government','military','meeting','discussed',
  'talks','conference','statement','spokesperson',
  'january','february','march','april','may','june','july','august',
  'september','october','november','december',
  'reuters','forbes','bbc','cnn','guardian','outside','movement',
  'international','national','communities','week','environment',
];

export function analyzeSentimentFromDescription(description: string | undefined): string {
  if (!description || typeof description !== 'string') return 'neutral';
  const desc = description.toLowerCase().trim();

  let filteredDesc = desc;
  for (const word of NEWS_FILTER_WORDS) {
    filteredDesc = filteredDesc.replace(new RegExp(`\\b${word}\\b`, 'gi'), ' ');
  }
  filteredDesc = filteredDesc.replace(/\s+/g, ' ').trim();
  if (!filteredDesc || filteredDesc.length < 10) return 'neutral';

  let positiveScore = 0;
  let negativeScore = 0;
  let neutralPenalty = 0;

  for (const [pattern, weight] of Object.entries(POSITIVE_PATTERNS)) {
    if (filteredDesc.includes(pattern)) positiveScore += weight;
  }
  for (const [pattern, weight] of Object.entries(NEGATIVE_PATTERNS)) {
    if (filteredDesc.includes(pattern)) negativeScore += weight;
  }
  for (const [pattern, weight] of Object.entries(NEUTRAL_PATTERNS)) {
    if (filteredDesc.includes(pattern)) neutralPenalty += Math.abs(weight);
  }

  if (neutralPenalty > 2) {
    positiveScore = Math.max(0, positiveScore - neutralPenalty * 0.5);
    negativeScore = Math.max(0, negativeScore - neutralPenalty * 0.5);
  }

  if (filteredDesc.split(/\s+/).length < 5 && neutralPenalty > 1) return 'neutral';

  const scoreDiff = Math.abs(positiveScore - negativeScore);
  const totalScore = positiveScore + negativeScore;
  if (totalScore < 2) return 'neutral';
  if (scoreDiff < totalScore * 0.3 && totalScore > 3) return 'mixed';
  if (negativeScore > positiveScore * 1.3 && negativeScore >= 3) return 'negative';
  if (positiveScore > negativeScore * 1.3 && positiveScore >= 2) return 'positive';
  if (negativeScore > positiveScore) return 'negative';
  if (positiveScore > negativeScore) return 'positive';
  return 'neutral';
}

export function getSentimentValue(sentiment: string | undefined): number {
  const map: Record<string, number> = { positive: 1, neutral: 0, negative: -1, mixed: 0 };
  return map[sentiment?.toLowerCase() ?? ''] ?? 0;
}

// ── Campaign-type classification ────────────────────────────────────────────

export function classifyHartEventByCampaignType(event: HartEvent): string {
  if (event.mlCampaignType && event.mlCampaignType !== 'Unknown') {
    return event.mlCampaignType;
  }
  const desc = (event.descBody || '').toLowerCase();
  let bestMatch = 'International';
  let bestScore = 0;
  for (const [campaignType, config] of Object.entries(ML_CAMPAIGN_TYPES)) {
    let score = 0;
    for (const keyword of config.keywords) {
      if (desc.includes(keyword)) score++;
    }
    if (score > bestScore) { bestScore = score; bestMatch = campaignType; }
  }
  return bestMatch;
}

// ── Risk-level determination ────────────────────────────────────────────────

export function determineRiskLevel(description: string): RiskInfo {
  const lower = (description || '').toLowerCase();
  if (HIGH_RISK_WORDS.some(w => lower.includes(w))) return { level: 'HIGH', color: '#f56565' };
  if (MED_RISK_WORDS.some(w => lower.includes(w)))  return { level: 'MEDIUM', color: '#ed8936' };
  if (LOW_RISK_WORDS.some(w => lower.includes(w)))   return { level: 'LOW', color: '#48bb78' };
  return { level: 'INFO', color: '#4299e1' };
}

// ── Trend-change calculation ────────────────────────────────────────────────

export function calculateTrendChange(currentCount: number, previousCount: number): TrendChange | null {
  if (!previousCount || previousCount === 0) {
    if (currentCount > 0) return { percent: '100', isRising: true, isFalling: false, isNew: true };
    return null;
  }
  const change = ((currentCount - previousCount) / previousCount) * 100;
  return { percent: Math.abs(change).toFixed(1), isRising: change > 0, isFalling: change < 0, isNew: false };
}

export function getTrendIndicatorHTML(trendChange: TrendChange | null | undefined): string {
  if (!trendChange) return '';
  if (trendChange.isRising) {
    return `<span style="display:inline-flex;align-items:center;gap:4px;color:#48bb78;font-weight:600;font-size:0.9em">
      <i class="fas fa-arrow-up"></i><span>+${trendChange.percent}%</span></span>`;
  }
  if (trendChange.isFalling) {
    return `<span style="display:inline-flex;align-items:center;gap:4px;color:#f56565;font-weight:600;font-size:0.9em">
      <i class="fas fa-arrow-down"></i><span>-${trendChange.percent}%</span></span>`;
  }
  return '';
}

// ── Date / location extraction from descriptions ────────────────────────────

export function extractDateFromDescription(description: string | undefined): string {
  if (!description) return 'Recent';
  const patterns = [
    /(\d{1,2}\s+(?:January|February|March|April|May|June|July|August|September|October|November|December)\s+\d{4})/i,
    /(\d{1,2}\/\d{1,2}\/\d{4})/,
    /(\d{4}-\d{2}-\d{2})/,
    /(yesterday|today|earlier)/i,
  ];
  for (const p of patterns) {
    const m = description.match(p);
    if (m) return m[1];
  }
  return 'Recent';
}

const LOCATION_PATTERNS = [
  /\b(Iraq|Iran|Syria|Yemen|Israel|Palestine|Gaza|Ukraine|Russia|Nigeria|Niger|Myanmar|Afghanistan|Pakistan|India|China|North Korea|South Korea|Lebanon|Turkey|Egypt|Libya|Sudan|Somalia|Mali|Chad|Burkina Faso|DR Congo|Rwanda|Ethiopia|Kenya|Uganda|Tanzania|Mozambique|Zimbabwe|South Africa)\b/i,
  /\b(Baghdad|Tehran|Damascus|Kyiv|Moscow|Kabul|Islamabad|New Delhi|Beijing|Seoul|Pyongyang|Beirut|Ankara|Cairo)\b/i,
];

export function extractLocationFromDescription(description: string | undefined): string {
  if (!description) return 'Unknown';
  for (const p of LOCATION_PATTERNS) {
    const m = description.match(p);
    if (m) return m[1];
  }
  return 'Unknown';
}

// ── Formatting helpers ──────────────────────────────────────────────────────

export function formatNumber(num: number): string {
  if (num >= 1_000_000) return (num / 1_000_000).toFixed(1) + 'M';
  if (num >= 1_000)     return (num / 1_000).toFixed(1) + 'K';
  return num.toString();
}

/** Relative date string ("Today", "Yesterday", "3 days ago", or locale). */
export function formatDateRelative(dateString: string): string {
  const date = new Date(dateString);
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);
  if (date.toDateString() === today.toDateString()) return 'Today';
  if (date.toDateString() === yesterday.toDateString()) return 'Yesterday';
  const diff = Math.floor((today.getTime() - date.getTime()) / 86_400_000);
  if (diff < 7) return `${diff} days ago`;
  return date.toLocaleDateString();
}

/** Locale-formatted date (e.g. "Jan 5, 2025"). */
export function formatDateLocale(dateString: string): string {
  return new Date(dateString).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
}

export function getSourceIcon(source: string | undefined): string {
  return SOURCE_ICON_MAP[source?.toLowerCase() ?? ''] ?? 'globe';
}

